import { useEffect, useMemo, useState } from "react";
import { Engraved, Icon, Script } from "../components/ui";
import type { ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import type { LightboxTile } from "../components/Lightbox";
import { MOMENTS, washFor } from "../lib/content";
import { useEvent } from "../lib/event";
import { api } from "../lib/api";

interface MomentDetailProps {
  moment: string | null;
  go: (s: ScreenId) => void;
  density: FloralDensity;
  openLightbox: (tiles: LightboxTile[], index: number) => void;
}

interface DetailTile {
  key: string;
  seed?: number;
  src?: string;
  video?: boolean;
}

export function MomentDetail({ moment, go, density, openLightbox }: MomentDetailProps) {
  const { eventId, live } = useEvent();
  const m = MOMENTS.find((x) => x.id === moment) || MOMENTS[0]!;
  const idx = MOMENTS.indexOf(m);

  const mockTiles: DetailTile[] = useMemo(
    () =>
      Array.from({ length: Math.min(m.count, 36) }, (_, i) => {
        const seed = m.count * 7 + i + idx * 13;
        return { key: String(seed), seed };
      }),
    [m.id, m.count, idx],
  );

  const [tiles, setTiles] = useState<DetailTile[]>(mockTiles);
  useEffect(() => setTiles(mockTiles), [mockTiles]);

  // Header count: mock until the live total arrives.
  const [count, setCount] = useState(m.count);
  useEffect(() => setCount(m.count), [m.id, m.count]);

  useEffect(() => {
    if (!live || !eventId || !moment) return;
    api
      .momentPhotos(eventId, moment)
      .then((r) => {
        setTiles(r.photos.map((p) => ({ key: p.photoId, src: p.fullUrl, video: p.kind === "video" })));
        setCount(r.total);
      })
      .catch((err) => console.warn("moment photos fetch failed:", err));
  }, [live, eventId, moment]);

  const lbTiles: LightboxTile[] = tiles.map((t) => ({ seed: t.seed, src: t.src, video: t.video }));

  return (
    <div className="screen mdetail">
      <div className="mdetail-hero" style={washFor(m.count + idx)}>
        <div className="ptile-grain" />
        <div className="mdetail-veil" />
        <FloralSpray variant="cornerTL" density={density} width={150} height={150} seed={idx + 2} className="mdetail-fl" />
        <button className="iconbtn floating" onClick={() => go("moments")} aria-label="Back">
          <Icon name="back" size={22} />
        </button>
        <div className="mdetail-hero-text">
          <Engraved size={11} style={{ color: "#fff", letterSpacing: "0.3em" }}>
            The Album
          </Engraved>
          <Script size={52} style={{ color: "#fff" }}>
            {m.name}
          </Script>
          <Engraved size={11} style={{ color: "rgba(255,255,255,0.92)", letterSpacing: "0.22em" }}>
            {count} photos · {m.desc}
          </Engraved>
        </div>
      </div>
      <div className="mdetail-grid">
        {tiles.map((t, i) => (
          <PhotoTile
            key={t.key}
            seed={t.seed}
            src={t.src}
            video={t.video}
            ratio={i % 7 === 0 ? "1 / 1.3" : "1 / 1"}
            radius={5}
            className={i % 7 === 0 ? "span2" : ""}
            onClick={() => openLightbox(lbTiles, i)}
          />
        ))}
      </div>
    </div>
  );
}
